import { useCallback, useEffect, useRef } from 'react';

import { ArrowLeftIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import {
  Button,
  DateInput,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_components';
import { Text } from '@/_components/typography/typography';
import { ActionButton } from '@/_containers';
import { dateTime, useBoolean } from '@/_shared';

import { durationOptions, now, yesterday } from './constants';
import { useCustomDateRange } from './use-custom-date-range';

export function DurationFilterMenu({
  value,
  onChange,
}: {
  value: { value: string; label: string };
  onChange: (value: { value: string; label: string }) => void;
}) {
  const [isOpen, open] = useBoolean();
  const [isCustom, custom] = useBoolean(value.label === 'Custom Date Range');
  const customerValueRef = useRef(false);
  const [state, setState] = useCustomDateRange();

  const handleSelect = useCallback(
    (option: { value: string; label: string }) => () => {
      if (option.value === 'custom') {
        customerValueRef.current = true;
        custom.setTrue();
      } else {
        onChange(option);
      }
    },
    [custom, onChange]
  );

  const handleOnChange = useCallback(
    (e: boolean) => {
      if (!customerValueRef.current) {
        open.set(e);
      }
    },
    [open]
  );

  const handleCloseCustom = useCallback(() => {
    custom.setFalse();
    setTimeout(() => {
      customerValueRef.current = false;
    }, 100);
  }, [custom]);

  const handleFromDateChange = useCallback(
    (date: string | null) => {
      setState((prev) => ({
        ...prev,
        from: date ?? dateTime.format(yesterday, 'YYYY-MM-DD'),
      }));
    },
    [setState]
  );

  const handleToDateChange = useCallback(
    (date: string | null) => {
      setState((prev) => ({
        ...prev,
        to: date ?? dateTime.format(now, 'YYYY-MM-DD'),
      }));
    },
    [setState]
  );

  const handleCustomDateChangeApply = useCallback(() => {
    const v = state.from + ',' + state.to;
    onChange({ value: v, label: 'Custom Date Range' });
    open.setFalse();
    // custom.setFalse();
    customerValueRef.current = false;
  }, [onChange, open, state.from, state.to]);

  useEffect(() => {
    if (value.value.includes(',')) {
      const [from, to] = value.value.split(',');
      setState({ from, to });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnChange}>
      <DropdownMenuTrigger asChild className="outline-none">
        <Button
          tag="div"
          iconLeft={CalendarIcon}
          iconRight={isOpen ? ChevronUpIcon : ChevronDownIcon}
          iconClassName="h-3.5"
          className="bg-c-background text-c-card-muted px-3 py-0"
        >
          {value?.label || 'Select Duration'}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {isCustom && (
          <div className="flex w-64 flex-col gap-6 p-4 py-2">
            <div className="-ml-2 flex items-center gap-1">
              <ActionButton aria-label="Back" className="opacity-80" icon={ArrowLeftIcon} onClick={handleCloseCustom} />
              <Text className="text-accent text-sm font-semibold dark:text-white">Select Custom Date Range</Text>
            </div>
            <DateInput
              value={state.from}
              maxDate={new Date(state.to)}
              renderInPortal={false}
              onValueChange={handleFromDateChange}
            />
            <DateInput
              value={state.to}
              minDate={dateTime.add(state.from, 1, 'day').toDate()}
              maxDate={now}
              renderInPortal={false}
              onValueChange={handleToDateChange}
            />

            <Button onClick={handleCustomDateChangeApply} size="sm">
              Apply
            </Button>
          </div>
        )}

        {!isCustom &&
          durationOptions.map((item) => (
            <DropdownMenuItem key={item.value} onClick={handleSelect(item)}>
              {item.label}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
